import React, { useCallback, useState, useContext } from "react";
import { Formik, Form } from "formik";
import { ILoginProps, ILoginFormValues, ILoginResponse } from "./LoginInterfaces";
import { adminLogin, HMILogin, corporateLogin } from "../../api/queries/loginAPI";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { loginSchema } from "../../validations/schemas";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { UserContext } from "../../contexts/UserContext/UserContext";
import { resetLocalStorage, setToken } from "../../utilities/storageUtilities";
import { IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useStyles } from "./Login.styles";
import {
    CORPORATE_BACKOFFICE_PATHS,
    HMI_PATHS,
    LocalStorageGlobalKeys,
    MADERA_BACKOFFICE_PATHS,
    ServerResponseErrorCodes,
    USERTYPE,
} from "constants/constants";
import useUptimeDowntimeActionsHMI from "hooks/useUptimeDowntimeActionsHMI";
import { MillOnboardingContext } from "contexts/MillOnboardingContext/MillOnboardingContext";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";

export const Login = ({ title, redirect }: ILoginProps) => {
    const { t } = useTranslation();
    const [loginError, setLoginError] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const { handleTokenAvailable, handleUserId } = useContext(UserContext);
    const {
        dispatch: { GetData },
    } = useContext(HMICachedDataContext);
    const {
        dispatch: { GetClusterData },
    } = useContext(ClusterCachedDataContext);
    const { handleHMILogin, handleClusterLogin } = useUptimeDowntimeActionsHMI();
    const navigate = useNavigate();
    const location = useLocation();
    const classes = useStyles();
    const pathname = location.pathname?.toLowerCase();
    const { isOnboardingFetch } = useContext(MillOnboardingContext);
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get("source");
    const millId = searchParams.get("mill_id");

    const handleSubmit = useCallback(
        async (values: ILoginFormValues) => {
            let loginResponse;
            resetLocalStorage([LocalStorageGlobalKeys.LANGUAGES, LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION]);

            if (pathname === `/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.LOGIN}`) {
                loginResponse = await adminLogin(values, handleUserId);
            }

            if (pathname.toLowerCase() === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`) {
                loginResponse = await HMILogin(values);
            }

            if (pathname === `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOGIN}`) {
                loginResponse = await corporateLogin(values, handleUserId);
            }

            if (loginResponse instanceof AxiosError) {
                setLoginError(true);

                if (loginResponse.response?.status === ServerResponseErrorCodes.UNAUTHORIZED) {
                    if (pathname === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`) {
                        setLoginErrorMessage(t("login.wrong_credentials_error_hmi"));
                    } else {
                        setLoginErrorMessage(t("login.wrong_credentials_error"));
                    }
                    return;
                }

                if (loginResponse.response?.status === ServerResponseErrorCodes.FORBIDDEN) {
                    if (pathname === `/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.LOGIN}`) {
                        setLoginErrorMessage(t("login.unauthorized_login_madera_backoffice"));
                    } else {
                        setLoginErrorMessage(t("login.unauthorized_login_HMI"));
                    }
                    return;
                }

                if (loginResponse.response?.status === ServerResponseErrorCodes.INTERNAL_SERVER_ERROR) {
                    setLoginErrorMessage(t("login.server_error"));
                    return;
                }

                if (loginResponse.response?.status === 404) {
                    setLoginErrorMessage(t("login.wrong_credentials_error"));
                    return;
                }
            }

            const { data } = loginResponse as ILoginResponse;

            if (data.token) {
                setLoginError(false);
                setLoginErrorMessage("");
                setToken(data.token);
                handleTokenAvailable(true);

                if (pathname.toLowerCase() === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`) {
                    try {
                        const isLogin = true;
                        if (data.machine_center) {
                            await GetData(data.machine_center.machine_center_id, isLogin);
                            await handleHMILogin(data.machine_center, isLogin);
                        } else if (data.cluster) {
                            await GetClusterData(data.cluster.cluster_id, isLogin);
                            await handleClusterLogin(data.cluster.machine_centers, isLogin);
                        }
                    } catch {
                        setLoginError(true);
                        setLoginErrorMessage(t("login.data_setup_error"));
                        return;
                    }
                }

                await isOnboardingFetch();

                if (redirect) {
                    if (
                        pathname.toLowerCase() ===
                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH.toLowerCase()}/${CORPORATE_BACKOFFICE_PATHS.LOGIN}`
                    ) {
                        if (source) {
                            if (source === "birch_notification") {
                                navigate(
                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}`,
                                );
                            } else if (source === "aspen_notification" && millId) {
                                navigate(
                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${millId}`,
                                );
                            }
                        } else {
                            if (
                                data.user.user_type.user_type_id === USERTYPE.ADMINISTRATOR ||
                                data.user.user_type.user_type_id === USERTYPE.EXECUTIVE
                            ) {
                                if (data.user.corporate.mills.length === 1) {
                                    navigate(
                                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${data.user.corporate.mills[0].mill_id}`,
                                    );
                                } else {
                                    navigate(
                                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.CORPORATE_DASHBOARD}`,
                                    );
                                }
                            } else {
                                navigate(
                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${
                                        CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT
                                    }/${data.user.mill_id || 0}`,
                                );
                            }
                        }
                    } else {
                        navigate(redirect);
                    }
                }
            }
        },
        [
            navigate,
            t,
            redirect,
            pathname,
            handleTokenAvailable,
            handleHMILogin,
            handleClusterLogin,
            isOnboardingFetch,
            handleUserId,
            GetClusterData,
            GetData,
            source,
            millId,
        ],
    );

    const initialValues =
        pathname === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`
            ? { email: "", password: "", showCode: true, code: "" }
            : { email: "", password: "" };

    const isHMILogin = pathname === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.LOGIN}`;
    const isCorporateLogin =
        pathname === `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOGIN}`;

    return (
        <Box className={classes.loginContainer}>
            <Box className={classes.loginCard}>
                <Box className={classes.loginLogo}>
                    <img src="/images/madera-login-logo.png" alt="Madera" />
                </Box>
                <Typography className={classes.loginTitle}>{title}</Typography>
                <Typography className={classes.loginSubtitle}>{t("login.login_subtitle")}</Typography>
                <Formik initialValues={initialValues} validationSchema={loginSchema} onSubmit={handleSubmit}>
                    {({ values, isSubmitting, errors, touched, setFieldValue }) => {
                        return (
                            <Form className={classes.loginForm}>
                                <Box className={classes.fieldsContainer}>
                                    <Box className={classes.fieldBlock}>
                                        <TextField
                                            id="outlined-email-input"
                                            type="text"
                                            placeholder={t("login.email_placeholder")}
                                            variant="outlined"
                                            autoFocus={true}
                                            value={values.email}
                                            onChange={(e) => setFieldValue("email", e.target.value as string)}
                                            className={classes.textField}
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && `${t(errors.email!)}`}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <MailOutlineIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.fieldBlock}>
                                        <Box className={classes.fieldLabelRow}>
                                            <Typography className={classes.fieldLabel}>
                                                {t("login.password")}
                                            </Typography>
                                            {isCorporateLogin && (
                                                <Link
                                                    className={classes.forgotPasswordLink}
                                                    to={`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.FORGOT_PASSWORD.ROOT}`}
                                                >
                                                    <Typography>{t("login.forgot_password")}</Typography>
                                                </Link>
                                            )}
                                        </Box>
                                        <TextField
                                            id="outlined-password-input"
                                            type={showPassword ? "text" : "password"}
                                            variant="outlined"
                                            value={values.password}
                                            onChange={(e) => setFieldValue("password", e.target.value as string)}
                                            className={classes.textField}
                                            error={touched.password && Boolean(errors.password)}
                                            helperText={touched.password && `${t(errors.password!)}`}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowPassword((show) => !show)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showPassword ? (
                                                                <VisibilityOffIcon />
                                                            ) : (
                                                                <VisibilityIcon />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                    {isHMILogin && (
                                        <Box className={classes.fieldBlock}>
                                            <Typography className={classes.fieldLabel}>{t("login.code")}</Typography>
                                            <TextField
                                                id="outlined-code-input"
                                                type="text"
                                                variant="outlined"
                                                value={values.code}
                                                onChange={(e) => setFieldValue("code", e.target.value as string)}
                                                className={classes.textField}
                                                error={touched.code && Boolean(errors.code)}
                                                helperText={touched.code && `${t(errors.code!)}`}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <VpnKeyOutlinedIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                {loginError && (
                                    <Box className={classes.errorMessage}>
                                        <Typography variant="error">{loginErrorMessage}</Typography>
                                    </Box>
                                )}
                                <Button
                                    className={classes.submitButton}
                                    variant="contained"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {t("login.submit")}
                                </Button>
                            </Form>
                        );
                    }}
                </Formik>
                <Box className={classes.secureRow}>
                    <Box className={classes.secureRowInner}>
                        <LockOutlinedIcon />
                        <Typography className={classes.secureRowText}>{t("login.secure_access")}</Typography>
                    </Box>
                </Box>
                <Box className={classes.helpRow}>
                    {t("login.need_help")}
                    <a className={classes.helpRowAction} href="mailto:info@maderasoftware.com">
                        {t("login.contact_us")}
                    </a>
                </Box>
            </Box>
            <Box className={classes.footer}>
                <span>
                    © {new Date().getFullYear()} MADERA. {t("login.rights_reserved")}
                </span>
            </Box>
        </Box>
    );
};
